import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, BookOpen, Clock, X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { INSTRUMENTS } from "@/data/curriculum";

const LEVELS = ["Beginner", "Intermediate", "Advanced"];
const RECENT_KEY = "jt_recent_searches";

function readRecent() {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(v) ? v.slice(0, 6) : [];
  } catch (e) {
    return [];
  }
}
function pushRecent(term) {
  const t = (term || "").trim();
  if (t.length < 2) return readRecent();
  const next = [t, ...readRecent().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 6);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch (e) {}
  return next;
}

// Flatten every lesson with its instrument/course context once.
const INDEX = INSTRUMENTS.flatMap((inst) =>
  inst.courses.flatMap((course) =>
    course.lessons.map((l, i) => ({
      lesson: l,
      instrument: inst,
      course,
      number: i + 1,
    }))
  )
);

export default function LessonSearch() {
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("all");
  const [recent, setRecent] = useState(readRecent);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    return INDEX.filter(({ lesson, instrument }) => {
      if (level !== "all" && lesson.level !== level) return false;
      if (!term) return true;
      const hay = `${lesson.title} ${lesson.topic} ${lesson.summary} ${lesson.level} ${instrument.label}`.toLowerCase();
      return term.split(/\s+/).every((w) => hay.includes(w));
    });
  }, [q, level]);

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 sm:px-8 py-10 lg:py-14">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Find a lesson</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Search lessons</h1>
        <p className="mt-4 max-w-xl text-muted-foreground">
          Search all {INDEX.length} lessons across every instrument by title, topic or level.
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-input"
              placeholder="e.g. treble clef, triplets, key signatures…"
              className="h-12 rounded-full border-border bg-card pl-11 text-base"
            />
          </div>
          <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="search-level-filter">
            {["all", ...LEVELS].map((lv) => (
              <button
                key={lv}
                onClick={() => setLevel(lv)}
                data-testid={`search-level-${lv.toLowerCase()}`}
                className={`rounded-full px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                  level === lv ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {lv}
              </button>
            ))}
          </div>
        </div>

        {!q.trim() && recent.length > 0 && (
          <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="search-recent">
            <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground"><Clock size={13} /> Recent</span>
            {recent.map((r) => (
              <button
                key={r}
                onClick={() => setQ(r)}
                data-testid={`search-recent-${r.replace(/\s+/g, "-").toLowerCase()}`}
                className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary"
              >
                {r}
              </button>
            ))}
            <button
              onClick={() => { try { localStorage.removeItem(RECENT_KEY); } catch (e) {} setRecent([]); }}
              data-testid="search-recent-clear"
              className="flex items-center gap-1 rounded-full px-2 py-1 text-xs text-muted-foreground hover:text-red-500"
            >
              <X size={12} /> Clear
            </button>
          </div>
        )}

        <p className="mt-5 font-mono text-xs uppercase tracking-widest text-muted-foreground" data-testid="search-count">
          {results.length} lesson{results.length === 1 ? "" : "s"}
        </p>

        {results.length === 0 ? (
          <p className="mt-6 rounded-2xl border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="search-empty">
            No lessons match "{q}". Try a different word.
          </p>
        ) : (
          <div className="mt-4 space-y-3" data-testid="search-results">
            {results.map(({ lesson, instrument, course, number }) => (
              <Link
                key={lesson.id}
                to={`/lessons/${lesson.id}`}
                onClick={() => setRecent(pushRecent(q))}
                data-testid={`search-result-${lesson.id}`}
                className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><BookOpen size={18} /></span>
                  <div>
                    <p className="font-medium text-foreground">{lesson.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{lesson.topic}</p>
                    <p className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="rounded-full bg-secondary px-2 py-0.5">{instrument.label}</span>
                      <span className="rounded-full bg-secondary px-2 py-0.5">{course.tagline}</span>
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">{lesson.level}</span>
                      <span>Lesson {number}</span>
                    </p>
                  </div>
                </div>
                <ArrowRight size={16} className="shrink-0 text-primary" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
